import { lazy } from 'react';
import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { ROLES } from './constants';

const RequireAuth = lazy(() => import('./RequireAuth'));
const AppLayout = lazy(() => import('../components/AppLayout/AppLayout'));
const PageNoFound = lazy(() => import('../components/PageNotFound/PageNoFound'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute/ProtectedRoute'));
const Forbidden = lazy(() => import('../components/Forbidden/Forbidden'));
const EntityManagementView = lazy(() => import('../components/EntityManagement/EntityManagementView'));
const AddVideo = lazy(() => import('../components/EntityManagement/Video/addVideo'));
const EntityDetails = lazy(() => import('../components/EntityManagement/EntityDetails/entityDetails'));
const JoinAsGuest = lazy(() => import('../components/EventsManagement/JoinAsGuest'));
const SignerWorkspace = lazy(() => import('../components/SignerWorkspace/SignerWorkspace'));

const Dashboard = lazy(() => import('../components/Dashboard/Dashboard'));
const UserProfile = lazy(() => import('../components/Proile/UserProfile'));

const SignIn = lazy(() => import('../components/Authentication/SignIn'));
const ForgotPassword = lazy(() => import('../components/Authentication/ForgotPassword'));
const ResetPassword = lazy(() => import('../components/Authentication/ResetPassword'));
const LanguageInterpretersManagement = lazy(() => import('../components/LanguageInterpreters/LanguageInterpretersManagement'));
const GuidesManagementView = lazy(() => import('../components/Guides/GuidesManagementView'));
const ContactManagementView = lazy(() => import('../components/ContactManagement/ContactManagementView'));
const ContentManagementView = lazy(() => import('../components/ContentManagement/ContentManagementView'));
const EntityCategoriesView = lazy(() => import('../components/EntityCategories/entityCategoriesView'));
const AddContent = lazy(() => import('../components/ContentManagementV2/AddContent'));
const ContentManagement = lazy(() => import('../components/ContentManagementV2/ContentManagement'));
const AssignmentManagement = lazy(() => import('../components/Assignments/AssignmentsManagement'));

const AddAssignment = lazy(() => import('../components/Assignments/AddAssignment'));
const TopicManagement = lazy(() => import('../components/TopicManagement/TopicManagement'));
const AddTopic = lazy(() => import('../components/TopicManagement/AddTopic'));

const EnrolledStudents = lazy(() => import('../components/AssignmentEvaluation/EnrolledUsers'));
const ReviewAssignments = lazy(() => import('../components/AssignmentEvaluation/ReviewAssignments'));
const EvaluateAssignment = lazy(() => import('../components/AssignmentEvaluation/EvaluateAssignment'));
const EventsManagement = lazy(() => import('../components/EventsManagement/EventsManagement'));
const EventViewMode = lazy(() => import('../components/EventsManagement/EventViewMode'));
const CreateEvent = lazy(() => import('../components/EventsManagement/CreateEvent'));
const LiveStreaming = lazy(() => import('../components/LiveStreaming/LiveStreaming'));
const NotAuthorized = lazy(() => import('../components/NotAuthorized/NotAuthorized'));
const HelpCenter = lazy(() => import('../components/HelpCenter/HelpCenter'));
export const routes: RouteObject[] = [
    { path: '/', element: <SignIn /> },
    { path: '/signIn', element: <SignIn /> },
    { path: '/forgotPassword', element: <ForgotPassword /> },
    { path: '/resetPassword', element: <ResetPassword /> },
    {
        element: <RequireAuth />,
        children: [
            {
                path: '/dashboard',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN]}>
                        <AppLayout pageTitle="Dashboard"><Dashboard /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/profile',
                element: (
                    <AppLayout pageTitle="User Profile"><UserProfile /></AppLayout>
                )
            },
            {
                path: '/interpreters',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN]}>
                        <AppLayout pageTitle="Signer"><LanguageInterpretersManagement pageTitle="Signer" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/guides',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN]}>
                        <AppLayout pageTitle="Guides"><GuidesManagementView pageTitle="Guides" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/contactManagement',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN]}>
                        <AppLayout pageTitle="Contact Management"><ContactManagementView pageTitle="Contact Management" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/postManagement',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN]}>
                        <AppLayout pageTitle="Post Management"><ContentManagementView pageTitle="Post Management" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/entityCategories',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN]}>
                        <AppLayout pageTitle="Entity Categories"><EntityCategoriesView pageTitle="Entity Categories" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/entityManagement',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN]}>
                        <AppLayout pageTitle="Entity Management"><EntityManagementView pageTitle="Entity Management" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/entityDetail/:id',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN]}>
                        <AppLayout pageTitle="Entity Management"><AddVideo pageTitle="Entity Management" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/entityDetail/:id/:activeTabName/:contentTabName?',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN]}>
                        <AppLayout pageTitle="Entity Management"><EntityDetails pageTitle="Entity Management" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/live/:eventId',
                element: <JoinAsGuest />
            },
            {
                path: '/events',
                element:
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <EventsManagement pageTitle="Events Management" />
                        </AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/events/add',
                element:
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN]}>
                        <AppLayout pageTitle="Content Management"><CreateEvent pageTitle="Create Event" /></AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/events/view/:eventId',
                element:
                    <AppLayout pageTitle="Content Management">
                        <EventViewMode pageTitle="View Event" />
                    </AppLayout>
            },
            {
                path: '/events/:eventId', element:
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN]}>
                        <AppLayout pageTitle="Content Management"><CreateEvent pageTitle="Update Event" /></AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content',
                element:
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <ContentManagement pageTitle="Content Management" />
                        </AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/add',
                element: (
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <AddContent pageTitle="Add Content" />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/add/:stepId',
                element: (
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <AddContent pageTitle="Add Content" />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/:stepId',
                element: (
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <AddContent pageTitle="Update Content" />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/assignments',
                element:
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <AssignmentManagement pageTitle="Assessment Management" />
                        </AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/:contentId/assignments/add', element:
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management"><AddAssignment pageTitle="Add Assessment" /></AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/:contentId/topics/:topicId/assignments',
                element:
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <AssignmentManagement pageTitle="Assessment Management" />
                        </AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/:contentId/topics/:topicId/assignments/add',
                element:
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management"><AddAssignment pageTitle="Add Assessment" /></AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/:contentId/assignments/:assignmentId',
                element:
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management"><AddAssignment pageTitle="Update Assessment" /></AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/:contentId/topics/:topicId/assignments/:assignmentId',
                element:
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management"><AddAssignment pageTitle="Update Assessment" /></AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/:contentId/topics',
                element:
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <TopicManagement pageTitle="Topic Management" />
                        </AppLayout>
                    </ProtectedRoute>
            },
            {
                path: '/content/:contentId/topics/add',
                element: (
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <AddTopic pageTitle="Add Topic" />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/topics/:topicId',
                element: (
                    <ProtectedRoute roles={[ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF, ROLES.SIGN_INTERPRETER]}>
                        <AppLayout pageTitle="Content Management">
                            <AddTopic pageTitle="Update Topic" />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/users',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF]}>

                        <AppLayout pageTitle="Assessment Evaluation">
                            <EnrolledStudents
                                pageTitle="Assessment Evaluation"
                            />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/topics/:topicId/users',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF]}>
                        <AppLayout pageTitle="Enrolled Students">
                            <EnrolledStudents
                                pageTitle="Enrolled Students"
                            />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/users/:userId/assignments',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF]}>

                        <AppLayout pageTitle="Review Assessments">
                            <ReviewAssignments pageTitle="Review Assessments" />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/topics/:topicId/users/:userId/assignments',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF]}>

                        <AppLayout pageTitle="Review Assignments">
                            <ReviewAssignments pageTitle="Review Assessments" fromTopic={true} />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            // Routes for EvaluateAssignment
            {
                path: '/content/:contentId/users/:userId/assignments/:assignmentId/evaluate',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF]}>
                        <AppLayout pageTitle="Evaluate Assessment">
                            <EvaluateAssignment pageTitle="Evaluate Assessment" />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },
            {
                path: '/content/:contentId/topics/:topicId/users/:userId/assignments/:assignmentId/evaluate',
                element: (
                    <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF]}>
                        <AppLayout pageTitle="Evaluate Assessment">
                            <EvaluateAssignment pageTitle="Evaluate Assessment" fromTopic={true} />
                        </AppLayout>
                    </ProtectedRoute>
                ),
            },


            {
                path: '/workspace',
                element: (
                    <ProtectedRoute roles={[ROLES.SIGN_INTERPRETER, ROLES.ENTITY_ADMIN, ROLES.ENTITY_STAFF]}>
                        <AppLayout pageTitle="Workspace"><SignerWorkspace pageTitle="Workspace" /></AppLayout>
                    </ProtectedRoute>
                )
            },
            {
                path: '/help-center/*',
                element: (
                    <AppLayout pageTitle="Help Center"><HelpCenter /></AppLayout>
                )
            },
            { path: '/empty', element: <AppLayout pageTitle=""><></></AppLayout> },
        ]
    },
    {
        path: '/not-authorized',
        element: <NotAuthorized />,
    },
    {
        path: '/forbidden',
        element: <Forbidden />,
    },

    // Fall through catch-all route
    {
        path: '/*',
        element: <PageNoFound />,
    },
];

export const initRouting = () => {
    const router = createBrowserRouter(routes);
    return router;
};